import { useEffect, useState } from 'react'
import { fetchCurrentUser, logout, type AuthenticatedUser } from '../services/auth'
import { APP_TITLE } from '../constants/branding'
import './Account.css'

export default function Account() {
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) || '/api'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const status = params.get('status')
    const msg = params.get('message')
    if (status === 'success') {
      setMessage({ type: 'success', text: 'Signed in with Discord successfully!' })
    } else if (status === 'error') {
      setMessage({ type: 'error', text: msg ?? 'Discord sign-in was cancelled or failed.' })
    }

    if (status) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await fetchCurrentUser()
        setUser(currentUser)
      } catch (error) {
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const handleSignIn = () => {
    window.location.href = `${apiBaseUrl.replace(/\/$/, '')}/auth/discord/start`
  }

  const handleLogout = async () => {
    try {
      await logout()
      setUser(null)
      setMessage({ type: 'success', text: 'Signed out successfully.' })
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to sign out. Please try again.' })
    }
  }

  return (
    <div className="account">
      <h1>Account</h1>
      {!user && !loading && (
        <p className="account-subtitle">
          Connect your Discord account to manage uploads, standings participation, and receive automated updates.
        </p>
      )}

      <div className="account-card">
        {loading ? (
          <div className="account-loading">Checking your account...</div>
        ) : (
          <>
            {user ? (
              <div className="account-profile">
                {user.discordAvatar ? (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.discordAvatar}.png?size=128`}
                    alt={user.username}
                    className="account-avatar"
                  />
                ) : (
                  <div className="account-avatar-placeholder">{user.username.charAt(0).toUpperCase()}</div>
                )}

                <div className="account-profile-details">
                  <h2>{user.discordGlobalName ?? user.username}</h2>
                  <p className="account-muted">Discord: {user.discordUsername ?? 'Connected'}</p>
                  {user.email && <p className="account-muted">Email: {user.email}</p>}

                  <div className="account-actions">
                    <button className="btn btn-secondary" onClick={handleLogout}>
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="account-auth-options">
                <button className="btn btn-primary" onClick={handleSignIn}>
                  Sign in with Discord
                </button>
                <p className="account-muted">
                  You will be redirected to Discord to authorize the {APP_TITLE}. We only request access to your
                  username and email for league coordination.
                </p>
              </div>
            )}

            {message && (
              <div className={`account-message account-message-${message.type}`}>
                {message.text}
              </div>
            )}

            {!user && (
              <div className="account-info">
                <h3>What you get</h3>
                <ul>
                  <li>Automatically link uploads and standings to your Discord identity</li>
                  <li>Receive Discord notifications for approvals and disputes</li>
                  <li>Access admin and commissioner tools (when granted)</li>
                  <li>Future single sign-on with additional league services</li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

